import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/admin/Modal';
import FormField from '../../components/admin/FormField';
import { rolesService, Role } from '../../services/admin.service';
import { Save, X } from 'lucide-react';

const RolesPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [formData, setFormData] = useState({ name: '' });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await rolesService.getAll();
      setRoles(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors du chargement des rôles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRole(null);
    setFormData({ name: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (role: Role) => {
    // Prevent editing admin role
    if (role.id === 1) {
      toast.error('Le rôle admin ne peut pas être modifié');
      return;
    }
    setEditingRole(role);
    setFormData({ name: role.name });
    setIsModalOpen(true);
  };

  const handleDelete = async (role: Role) => {
    // Prevent deleting admin role
    if (role.id === 1) {
      toast.error('Le rôle admin ne peut pas être supprimé');
      return;
    }
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${role.name}"?`)) {
      try {
        await rolesService.delete(role.id);
        toast.success('Rôle supprimé avec succès');
        loadRoles();
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  // Check if role can be edited (admin role with id: 1 cannot be edited)
  const canEditRole = (role: Role) => role.id !== 1;

  // Check if role can be deleted (admin role with id: 1 cannot be deleted)
  const canDeleteRole = (role: Role) => role.id !== 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent editing admin role
    if (editingRole && editingRole.id === 1) {
      toast.error('Le rôle admin ne peut pas être modifié');
      return;
    }

    try {
      if (editingRole) {
        await rolesService.update(editingRole.id, formData);
        toast.success('Rôle mis à jour avec succès');
      } else {
        await rolesService.create(formData);
        toast.success('Rôle créé avec succès');
      }
      setIsModalOpen(false);
      setFormData({ name: '' });
      setEditingRole(null);
      loadRoles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de l\'opération';
      toast.error(errorMessage);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const columns = [
    {
      key: 'id',
      header: 'ID',
    },
    {
      key: 'name',
      header: 'Nom',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Gestion des Rôles</h1>
        <p className="text-text-secondary mt-2">Gérer les rôles des utilisateurs</p>
      </div>

      <DataTable
        data={filteredRoles}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canEdit={canEditRole}
        canDelete={canDeleteRole}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <FormField label="Nom du rôle" required>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              required
            />
          </FormField>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-text-secondary hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{editingRole ? 'Enregistrer' : 'Créer'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RolesPage;

